import React, {useState, useContext, useRef, useEffect} from 'react';
import {Badge, Spinner} from "react-bootstrap";
import {DateTime} from "luxon";
import {PowerSources} from "../../Enums/WaterSystemsPage/powerSource";
import Table, {
    buildMultiSelectFilterRenderer, buildSelectFilter, likeCompMultiSelectFilter, tableTextFilter
} from "../Table/Table";
import {EnumToList} from "../../Enums/EnumToList";
import {meterTypesDictionary} from "../../Pages/WaterSystems/meterTypeDictionary";
import {
    DATE_LOCALE, WINT_AI_CONTROL_URL, WINT_ORGANIZATIONS_ROUTE, WINT_SITES_ROUTE, WINT_WATER_SYSTEMS_ROUTE
} from "../../Constants/constants";

import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';
import './WaterSystemsTable.css'
import {store} from "../../Store/store";
import {DateFormatter} from "../../General/SharedMethods";

export default function WaterSystemsTable({
                                              countriesFilterOptions,
                                              data,
                                              getAllWaterSystems,
                                              isRequestInProgress,
                                              setSelectedSystemStates
                                          }) {
    const globalState = useContext(store);

    const NUMBER_OF_FILTERS = 23;

    // region Formatters

    const leakStatusFormatter = (cell, row) => {
        let variant;

        switch (row.leakStatus) {
            case 'Major': {
                variant = 'danger';
                break;
            }
            case 'Moderate': {
                variant = 'orange';
                break;
            }
            case 'Minor': {
                variant = 'warning';
                break;
            }
            case 'No Leak': {
                variant = 'success';
                break;
            }
            default: {
                variant = 'light';
                break;
            }
        }

        return <Badge variant={variant}>{row.leakStatus}</Badge>
    }

    const waterSystemFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.siteId}/${WINT_WATER_SYSTEMS_ROUTE}/${row.waterSystemId}`;
        return <a target="_blank" rel="noreferrer" href={href}>{row.waterSystemName}</a>
    }

    const siteFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.siteId}`;
        return <a target="_blank" rel="noreferrer" href={href}>{row.siteName}</a>
    }

    const organizationFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_ORGANIZATIONS_ROUTE}/${row.organizationId}/`;
        return <a target="_blank" rel="noreferrer" href={href}>{row.organizationName}</a>
    }

    const valveStatusFormatter = (cell, row) => {
        let variant;

        switch (row.valveStatus) {
            case 'Open': {
                variant = 'success';
                break;
            }
            case 'Closed': {
                variant = 'danger';
                break;
            }
            case 'Valve Error': {
                variant = 'error';
                break;
            }
            case 'Physical Override': {
                variant = 'orange';
                break;
            }
            default: {
                variant = 'light';
                break;
            }
        }

        return <Badge variant={variant}>{row.valveStatus}</Badge>
    }

    const dateFormatter = (cell) => DateFormatter(cell);

    const cellNumFormatter = (cell) => {
        if (!cell) return '';

        if (cell.length < 12) return cell;

        return `+${cell[0]}${cell[1]}${cell[2]} ${cell[3]}${cell[4]}-${cell[5]}${cell[6]}${cell[7]}-${cell[8]}${cell[9]}${cell[10]}${cell[11]}`
    }

    const valveStatusCsvFormatter = (cell, row) => {
        return row.valveStatus;
    }

    const powerSourceFormatter = (cell, row) => {
        if (cell === 'AC Unplugged') {
            return <p style={{color: 'red', fontWeight: 700, margin: 0}}>{cell}</p>
        }

        return cell;
    }

    const batStatFormatter = (cell, row) => {
        if (cell < 2950) {
            return <p style={{color: 'red', fontWeight: 700, margin: 0}}>{cell}</p>
        }

        return cell;
    }

    const autoShutoffFormatter = (cell, row) => {
        let variant;
        let textValue;
        switch (cell) {
            case 1:
                variant = "success";
                textValue = "Enabled";
                break;
            case 2:
                variant = "light";
                textValue = "N/A";
                break;
            default:
                variant = "danger";
                textValue = "Disabled";
        }

        return <Badge variant={variant}>{textValue}</Badge>
    }

    const autoShutoffCsvFormatter = (cell, row) => {
        return (cell === 0) ? "Disabled" : "Enabled";
    }

    const deviceRNCsvFormatter = (cell, row) => {
        return row.deviceRn.toString();
    }

    // endregion

    // region Filters

    const filtersRefs = useRef([]);
    const countriesFilterRef = useRef();
    const valveTypeFilterRef = useRef();
    const meterTypeFilterRef = useRef();
    const dataModeFilterRef = useRef();
    const powerSourceFilterRef = useRef();
    const valveStatusFilterRef = useRef();
    const leakStatusFilterRef = useRef();
    const detectionModeFilterRef = useRef();

    const resetFilters = () => {
        if (filtersRefs.current.length > NUMBER_OF_FILTERS) {
            const startIndex = filtersRefs.current.length - NUMBER_OF_FILTERS;
            filtersRefs.current = filtersRefs.current.slice(startIndex);
        }
        filtersRefs.current.forEach(currFilter => {
            if (currFilter) currFilter('');
        })

        countriesFilterRef.current.resetFilter();
        valveTypeFilterRef.current.resetFilter();
        meterTypeFilterRef.current.resetFilter();
        powerSourceFilterRef.current.resetFilter();
        valveStatusFilterRef.current.resetFilter();
        leakStatusFilterRef.current.resetFilter();
        detectionModeFilterRef.current.resetFilter();
        dataModeFilterRef.current.resetFilter();
    }

    const detectionModeFilterOptions = [{value: 'adaptive', label: 'Adaptive'}, {
        value: 'Fixed - Event Based', label: 'Fixed - Event Based'
    }, {value: 'Fixed - Cumulative', label: 'Fixed - Cumulative'},]

    const valveStatusFilterOptions = [
        {value: 'Open', label: 'Open'},
        {value: 'Closed', label: 'Closed'},
        {value: 'Valve Error', label: 'Valve Error'},
        {value: 'Physical Override', label: 'Physical Override'},
        {value: 'N/A', label: 'N/A'}
    ];

    const autoShutoffFilterOptions = {
        0: 'Disabled',
        1: 'Enabled',
        2: 'N/A'
    };

    const leakStatusFilterOptions = [{value: 'No Leak', label: 'No Leak'}, {
        value: 'Major', label: 'Major'
    }, {value: 'Moderate', label: 'Moderate'}, {value: 'Minor', label: 'Minor'}]

    const valveTypeFilterOptions = [
        {value: 'Solenoid Plastic', label: 'Solenoid Plastic'},
        {value: 'Solenoid Metal', label: 'Solenoid Metal'},
        {value: 'Ball Valve', label: 'Ball Valve'},
        {value: 'Butterfly', label: 'Butterfly'},
        {value: 'No Valve', label: 'No Valve'},
    ];

    const meterTypeFilterOptions = () => {
        let options = [];

        for (const meterTypeId in meterTypesDictionary) {
            let value = `${meterTypesDictionary[meterTypeId].displayName} (${meterTypesDictionary[meterTypeId].meterType})`
            options.push({value: value, label: value});
        }

        return options;
    }

    const dataModeFilterOptions = () => {
        return [{value: 'Data Mode', label: 'Data Mode'}, {value: 'Failure', label: 'Failure'}, {
            value: 'SMS', label: 'SMS'
        }, {value: 'Fallback', label: 'Fallback'},];
    }

    // endregion

    // region Styles

    const headerStyle = {fontSize: 13};
    const filterLessHeaderStyle = {fontSize: 13, paddingBottom: 27};
    const colStyle = {fontSize: 12, padding: '1px', verticalAlign: 'center'};
    const capitalizeColStyle = {fontSize: 12, padding: '1px', verticalAlign: 'center', textTransform: 'capitalize'};

    // endregion

    // region Table Properties

    const columns = [{
        dataField: 'siteCountry',
        adminColumn: true,
        text: 'Country',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(countriesFilterRef, countriesFilterOptions)
    }, {
        dataField: 'organizationName',
        text: 'Organization',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs),
        formatter: organizationFormatter
    }, {
        dataField: 'siteName',
        text: 'Site',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs),
        formatter: siteFormatter
    }, {
        dataField: 'waterSystemName',
        isDefault: true,
        text: 'Water System',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs),
        formatter: waterSystemFormatter
    }, {
        dataField: 'lifecyclePhase',
        text: 'System Life Cycle State',
        sort: true,
        headerStyle: filterLessHeaderStyle,
        style: colStyle,
        editable: false
    }, {
        dataField: 'leakStatus',
        text: 'Leak Status',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        formatter: leakStatusFormatter,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(leakStatusFilterRef, leakStatusFilterOptions)
    }, {
        dataField: 'policyName',
        text: 'Policy Name',
        headerStyle: headerStyle,
        style: capitalizeColStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'autoWaterShutoff',
        text: 'Auto Water Shutoff',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        formatter: autoShutoffFormatter,
        filter: buildSelectFilter(filtersRefs, autoShutoffFilterOptions),
        csvFormatter: autoShutoffCsvFormatter
    }, {
        dataField: 'valveStatus',
        text: 'Valve Status',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        formatter: valveStatusFormatter,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(valveStatusFilterRef, valveStatusFilterOptions),
        csvFormatter: valveStatusCsvFormatter
    }, {
        dataField: 'powerSource',
        text: 'Power Source',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(powerSourceFilterRef, EnumToList(PowerSources)),
        formatter: powerSourceFormatter
    }, {
        dataField: 'valveType',
        text: 'Valve Type',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(valveTypeFilterRef, valveTypeFilterOptions)
    }, {
        dataField: 'meterType',
        text: 'Meter Type',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(meterTypeFilterRef, meterTypeFilterOptions())
    }, {
        dataField: 'detectionMode',
        text: 'Detection Mode',
        headerStyle: headerStyle,
        style: capitalizeColStyle,
        sort: true,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(detectionModeFilterRef, detectionModeFilterOptions)
    }, {
        dataField: 'thresholds',
        adminColumn: true,
        text: 'Thresholds (L)',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'deviceRn',
        text: 'Device RN',
        headerStyle: filterLessHeaderStyle,
        style: colStyle,
        sort: true,
        csvFormatter: deviceRNCsvFormatter
    }, {
        dataField: 'batStat',
        adminColumn: true,
        text: 'Battery Value',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs),
        formatter: batStatFormatter
    }, {
        dataField: 'batteryVoltage',
        adminColumn: true,
        text: 'Battery (V)',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs),
        formatter: (cell, row) => !row.batteryVoltage ? '' : `${row.batteryVoltage.toLocaleString()}`,
    }, {
        dataField: 'currentVersion',
        adminColumn: true,
        text: 'Version',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'lastKeepAlive',
        adminColumn: true,
        text: 'Last Keep Alive',
        headerStyle: filterLessHeaderStyle,
        style: colStyle,
        sort: true,
        formatter: dateFormatter
    }, {
        dataField: 'cellNum',
        adminColumn: true,
        text: 'Sim Number',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs),
        formatter: cellNumFormatter
    }, {
        dataField: 'waterSystemId',
        adminColumn: true,
        text: 'Water System ID',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'siteId',
        adminColumn: true,
        text: 'Site ID',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'organizationId',
        adminColumn: true,
        text: 'Organization ID',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'username',
        adminColumn: true,
        text: 'User Name',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: tableTextFilter(filtersRefs)
    }, {
        dataField: 'systemInstallationDate',
        adminColumn: true,
        text: 'Installation Date',
        headerStyle: filterLessHeaderStyle,
        style: colStyle,
        sort: true,
        formatter: dateFormatter
    }, {
        dataField: 'systemReplacementDate',
        adminColumn: true,
        text: 'Replacement Date',
        headerStyle: filterLessHeaderStyle,
        style: colStyle,
        sort: true,
        formatter: dateFormatter
    }, {
        dataField: 'dataMode',
        adminColumn: true,
        text: 'Data Mode',
        headerStyle: headerStyle,
        style: colStyle,
        sort: true,
        filter: likeCompMultiSelectFilter,
        filterRenderer: buildMultiSelectFilterRenderer(dataModeFilterRef, dataModeFilterOptions())
    }]

    // endregion

    return (<>
        <div style={{overflowX: 'auto', width: '100%', maxWidth: '100vw', height: '50.7em'}}>
            <Table
                data={data}
                columns={columns}
                pageSize={28}
                exportCsv
                csvFileName={`Water_Systems_Report_${DateTime.local().toLocaleString({locale: DATE_LOCALE})}.csv`}
                resetFilters={resetFilters}
                systemCycleCallBack={getAllWaterSystems}
                isRequestInProgress={isRequestInProgress}
                setSelectedSystemStates={setSelectedSystemStates}
            />
        </div>
    </>)
}
